import {
	InterventionDefinitionTypeEnum,
	InterventionTaskStatusEnum,
	PerformedByEnum,
	type DiagnosisViewModel,
	type InterventionTaskCommandModel,
	type InterventionTaskViewModel
} from '@generated';
import { convertClientValuesToServerValues, getInitialValues } from '~/libraries/forms/values';
import type { FormRegistryValues } from '~/shared/forms/FormRegistryContext';
import type { FormValues } from '~/shared/forms/types';

const today = new Date();

export function getInterventionDocumentationInitialValues(
	interventionTask: InterventionTaskViewModel,
	allInterventionTasks: InterventionTaskViewModel[],
	stayFindings: string[],
	visitFindings: string[],
	diagnoses: DiagnosisViewModel[] | undefined
): FormValues {
	const baseValues = {
		performed: true,
		patientToleratedId: interventionTask.patientToleratedId ?? null,
		performedById: interventionTask.performedById ? interventionTask.performedById : PerformedByEnum.Clinician,
		performedOn: interventionTask.performedOn ?? today,
		notes: interventionTask.notes ?? '',
		markAsNotPerformed: interventionTask.interventionTaskStatusId === InterventionTaskStatusEnum.DidNotPerform,
		didNotPerformReasonId: interventionTask.didNotPerformReasonId ?? null,
		didNotPerformReasonOther: interventionTask.didNotPerformReasonOther,
		instructionTargetPatient: interventionTask.instructionTargetPatient,
		instructionTargetCaregiver: interventionTask.instructionTargetCaregiver,
		instructionTargetOther: interventionTask.instructionTargetOther,
		instructionResponseId: interventionTask.instructionResponseId ?? null
	};

	if (interventionTask.intervention && interventionTask.intervention.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Custom) {
		return baseValues;
	} else if (!interventionTask.interventionDefinition) {
		throw new Error('Intervention definition is not defined for the intervention task');
	}

	const initialTaskValues = getInitialValues(interventionTask.interventionDefinition.documentationFormFields, interventionTask.values);
	const externalReferences = interventionTask.interventionDefinition.externalReferences ?? [];

	const externalReferenceTaskValues = allInterventionTasks
		.filter(
			x => x.interventionDefinition && x.interventionDefinition.documentationFormFields.some(field => externalReferences.includes(field.name))
		)
		.reduce((acc, referencedTask) => {
			if (referencedTask.values) {
				acc = { ...acc, ...getInitialValues(referencedTask.interventionDefinition!.documentationFormFields, referencedTask.values) };
			}
			return acc;
		}, {} as Record<string, unknown>);

	return {
		...initialTaskValues,
		...externalReferenceTaskValues,
		performed: true,
		patientToleratedId: interventionTask.patientToleratedId ?? null,
		performedById: interventionTask.performedById ? interventionTask.performedById : PerformedByEnum.Clinician,
		performedOn: interventionTask.performedOn ?? today,
		notes: interventionTask.notes ?? '',
		markAsNotPerformed: interventionTask.interventionTaskStatusId === InterventionTaskStatusEnum.DidNotPerform,
		didNotPerformReasonId: interventionTask.didNotPerformReasonId ?? null,
		didNotPerformReasonOther: interventionTask.didNotPerformReasonOther,
		instructionTargetPatient: interventionTask.instructionTargetPatient,
		instructionTargetCaregiver: interventionTask.instructionTargetCaregiver,
		instructionTargetOther: interventionTask.instructionTargetOther,
		instructionResponseId: interventionTask.instructionResponseId ?? null,
		stayFindings: stayFindings,
		visitFindings: visitFindings,
		diagonsesCodes: diagnoses?.map(diagnosis => diagnosis.icdDiagnosis.code) || []
	};
}

export function getDidNotPerformInterventionInitialValues(interventionTask: InterventionTaskViewModel): FormValues {
	return {
		performed: false,
		didNotPerformReasonId: interventionTask.didNotPerformReasonId ?? null,
		didNotPerformReasonOther: interventionTask.didNotPerformReasonOther
	};
}

export function convertToServerValues(
	tasks: InterventionTaskViewModel[],
	formRegisteryValues: FormRegistryValues
): { interventionTasks: InterventionTaskCommandModel[] } {
	const interventionTasks: InterventionTaskCommandModel[] = tasks.map(task => {
		const values = formRegisteryValues[task.id] ?? {};
		const performed = values.performed;

		if (!performed) {
			return {
				interventionTaskId: task.id,
				performed: false,
				didNotPerformReasonId: values.didNotPerformReasonId ?? undefined,
				didNotPerformReasonOther: values.didNotPerformReasonOther,
				notes: values.notes
			};
		}

		const baseValues = {
			interventionTaskId: task.id,
			performed: true,
			notes: values.notes,
			patientToleratedId: values.patientToleratedId ?? undefined,
			performedById: values.performedById ?? undefined,
			performedOn: values.performedOn,
			patientInstructed: values.instructionTargetPatient,
			caregiverInstructed: values.instructionTargetCaregiver,
			otherInstructed: values.instructionTargetOther,
			instructionResponseId: values.instructionResponseId ?? undefined
		};

		if (task.intervention && task.intervention.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Custom) {
			return baseValues;
		} else if (!task.interventionDefinition) {
			throw new Error('Intervention definition is not defined for the intervention task');
		}

		const documentationFormFields = task.interventionDefinition.documentationFormFields;
		return {
			...baseValues,
			systemName: task.interventionDefinition.systemName,
			version: task.interventionDefinition.version,
			values:
				documentationFormFields && documentationFormFields.length > 0
					? convertClientValuesToServerValues(values, documentationFormFields)
					: undefined
		};
	});

	return { interventionTasks };
}
